import { get } from 'lodash';

import {
	getAncestorPinnedListing,
	getBoundListingId,
	isBoundListingResolving,
	resolveBoundListing,
} from './block';
import { hasMergeTagKey, normalizeMergeTagKey } from './mergeTagDictionary';
import { resolveAttachedListingMergeTags } from './resolveAttachedListingMergeTags';
import type { IBlockData } from '@core/typings';
// `import type`, deliberately: a value import of @core/blocks from a util
// recreates the block-registry cycle (see listingScope.ts's note).
import type { IPage } from '@core/blocks';

export interface ResolveMergeTagDisplayValueParams {
	tagKey: string;
	blockIdx?: string | null;
	content: IPage;
	/**
	 * The viewer's REAL profile fields, not sample merge-tag data. Office sends a
	 * fixed key set whose values may be undefined (client's EditorLayout builds it
	 * as one object literal and passes it through unfiltered), so a key being
	 * PRESENT is what makes a blank value meaningful here.
	 */
	realUserProfile?: Record<string, unknown> | null;
	/** Platform discriminator, not a role check: Design Manager true, Office false. */
	isDesignAdmin?: boolean;
}

/**
 * What a merge-tag chip on the editor canvas should display, as one of three
 * signals consumed by `pickBadgeDisplayText`:
 *
 * - a non-empty string — the real resolved value;
 * - `''` — eligible, looked it up, and the field is genuinely empty. This is the
 *   ONLY case that earns the chip's configured fallback text;
 * - `undefined` — not eligible, so the field label stands.
 *
 * Scope is the ruling on subitem 12981869860: fallback text applies in the
 * Office editor only, to all User fields the profile carries, and to Listing
 * fields only when a listing is actually selected. Everything else keeps the
 * label.
 */
export function resolveMergeTagDisplayValue({
	tagKey,
	blockIdx,
	content,
	realUserProfile,
	isDesignAdmin,
}: ResolveMergeTagDisplayValueParams): string | undefined {
	// Design Manager never earns a fallback - there the field label IS the
	// intended display (see getPlaceholder's doc comment on this discriminator).
	const blankResolved = isDesignAdmin ? undefined : '';

	if (tagKey.startsWith('user.')) {
		const field = tagKey.slice('user.'.length);
		// Key present + blank value = the account genuinely has no value, which
		// earns the fallback. Key absent = a host that does not carry this field
		// at all, or a profile that has not loaded yet; falling back there would
		// print the fallback over a value the sent email renders for real.
		if (!realUserProfile || !(field in realUserProfile)) return undefined;
		const trimmed = String(realUserProfile[field] ?? '').trim();
		return trimmed ? trimmed : blankResolved;
	}

	// A listing chip resolves against whatever listing its own block is bound to,
	// or the one its Listing Wrapper / column pins - same precedence the blocks
	// themselves use.
	if (!tagKey.startsWith('listing.') || !blockIdx) return undefined;
	// Wrapped, not bare: an idx string is rooted at getPageIdx() === 'content',
	// so it has to be looked up against { content }, never against the page
	// object itself - `get(page, 'content.children.[0]')` is always undefined.
	// Same shape block.ts:212 uses.
	const block = get({ content }, blockIdx) as IBlockData | undefined;

	// A key outside the merge-tag dictionary is not a field at all. Checked
	// BEFORE resolving, because the resolver cannot tell us this afterwards.
	//
	// Normalized first: hasMergeTagKey is an exact dictionary lookup, while
	// resolveAttachedListingMergeTags resolves legacy aliases too
	// (listing.stateProvince, listing.postalCode, the image aliases...). Gating
	// on the raw key would call every alias "not a field" and print its label
	// over a value that resolves fine - the reported defect, on a different row.
	if (!hasMergeTagKey(normalizeMergeTagKey(tagKey))) return undefined;

	const listing =
		getAncestorPinnedListing(content as any, blockIdx) ||
		resolveBoundListing(block?.data?.value as any);

	// Eligibility comes from the BINDING, not from the resolved record. The
	// resolved helpers return null both for "no listing picked" and for one that
	// is still loading or has gone unavailable, and those do not render the same:
	// resolveBoundListing's own contract says an unavailable listing should land
	// on the configured fallback, and returning the label there is the reported
	// defect for a chip that does have a listing selected.
	if (!listing) {
		const boundListingId = getBoundListingId(content as any, blockIdx, block);
		// Nothing picked - the label stands, per the ruling on 12981869860.
		if (!boundListingId) return undefined;
		// Picked, lookup still in flight - hold the label rather than flashing the
		// fallback and then swapping to a real value.
		if (isBoundListingResolving(content as any, blockIdx, block)) return undefined;
		// Picked, lookup finished, listing gone (sold, deleted, unavailable): the
		// field has no value, which is exactly what the fallback is for.
		return blankResolved;
	}

	const resolved = resolveAttachedListingMergeTags(`{{${tagKey}}}`, { listing });
	// resolveAttachedListingMergeTags hands back the raw `{{tag}}` for BOTH a
	// blank field and an unknown one, deliberately (its own comments say so), so
	// an unresolved token here cannot be read as "unknown key" - the dictionary
	// check above already ruled that out. A listing IS bound and answered with
	// nothing, which means the field is empty: the case that earns the fallback.
	// Reading it the other way is what left the canvas printing "County" while
	// Preview & Test printed "Elko County".
	if (resolved.includes('{{')) return blankResolved;
	return resolved.trim() || blankResolved;
}
